/**
 * Step 1 — 회사 정보 입력. FE-W-04.
 */

import { useState, useSyncExternalStore, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { RestoreBanner } from '../shared/RestoreBanner';
import { tOnb, getOnboardingLang, subscribeOnboardingLang } from '../../../lib/onboarding-i18n';

const MAX_LEN = 60;

export function Step1CompanyForm() {
  useSyncExternalStore(subscribeOnboardingLang, getOnboardingLang, getOnboardingLang);
  const navigate = useNavigate();
  const start = useOnboardingStore((s) => s.start);
  const loading = useOnboardingStore((s) => s.loading);
  const error = useOnboardingStore((s) => s.error);
  const clearError = useOnboardingStore((s) => s.clearError);
  const storedCompanyName = useOnboardingStore((s) => s.companyName);

  const [companyName, setCompanyName] = useState<string>(storedCompanyName ?? '');

  const trimmed = companyName.trim();
  const canSubmit = trimmed.length > 0 && trimmed.length <= MAX_LEN && !loading;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    clearError();
    try {
      await start({ companyName: trimmed });
      // HIGH-3 봉합 — start idempotent 응답 시 progressed currentStep 으로 분기.
      // 이미 Step3 진행 중인 session 재진입 시 /onboarding/verify 로 갈 것.
      const nextStep = useOnboardingStore.getState().currentStep;
      const targets: Record<number, string> = {
        1: '/onboarding/domain',
        2: '/onboarding/domain',
        3: '/onboarding/verify',
        4: '/onboarding/admin',
      };
      navigate(targets[nextStep] ?? '/onboarding/domain');
    } catch {
      /* error 는 store.error 에 반영됨 */
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} noValidate>
      <RestoreBanner />

      <h2 className="onboarding-step__heading">{tOnb('onboarding.step.label.company')}</h2>
      <p className="onboarding-step__subheading">
        {tOnb('onboarding.step1.subheading')}
      </p>

      {error && (
        <div className="onboarding-step__alert" role="alert">
          {error.message}
        </div>
      )}

      <div className="onboarding-step__field">
        <label htmlFor="companyName" className="onboarding-step__label">
          {tOnb('onboarding.step1.label_name')}
        </label>
        <input
          id="companyName"
          name="companyName"
          type="text"
          className="onboarding-step__input"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          maxLength={MAX_LEN}
          placeholder={tOnb('onboarding.step1.placeholder_name')}
          autoFocus
          autoComplete="organization"
          required
          aria-describedby="companyName-hint"
        />
        <p id="companyName-hint" className="onboarding-step__hint">
          {tOnb('onboarding.step1.hint', { max: MAX_LEN })}
        </p>
      </div>

      <div className="onboarding-step__actions">
        <button
          type="submit"
          className="onboarding-step__button onboarding-step__button--primary"
          disabled={!canSubmit}
          aria-busy={loading}
        >
          {loading ? tOnb('onboarding.btn.processing') : tOnb('onboarding.btn.next')}
        </button>
      </div>
    </form>
  );
}
